import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import { Check, ArrowRight, Sparkle } from "@phosphor-icons/react";

export default function Billing() {
  const [plans, setPlans] = useState({});
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState("");
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const [status, setStatus] = useState(null);

  useEffect(() => {
    api.get("/billing/plans").then(({ data }) => setPlans(data));
    api.get("/billing/me").then(({ data }) => setMe(data));
  }, []);

  // Poll session if returning from Stripe
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/billing/status/${sid}`);
        setStatus(data);
        if (data.payment_status === "paid") {
          // Refresh subscription
          const r = await api.get("/billing/me"); setMe(r.data);
          params.delete("session_id"); setParams(params);
          return;
        }
        if (data.status === "expired" || attempts > 8) return;
        attempts++;
        setTimeout(poll, 2000);
      } catch {}
    };
    poll();
    // eslint-disable-next-line
  }, []);

  const subscribe = async (id) => {
    if (id === "starter") return;
    if (id === "enterprise") { window.location.href = "mailto:pankaj@span.com?subject=SPAN Enterprise plan enquiry"; return; }
    setBusy(id);
    try {
      const { data } = await api.post("/billing/checkout", { plan_id: id, origin_url: window.location.origin });
      window.location.href = data.url;
    } finally { setBusy(""); }
  };

  return (
    <div className="space-y-6" data-testid="billing-page">
      <div>
        <div className="label-eyebrow">Subscription</div>
        <h1 className="font-display text-[28px] font-bold tracking-tight mt-1">Billing & Plans</h1>
        <div className="text-[12px] text-[var(--span-text-2)] mt-1">
          Powered by Stripe · current plan: <span className="font-mono font-semibold">{(me?.plan_id || "starter").toUpperCase()}</span>
          {me?.subscription?.current_period_end && <> · renews {new Date(me.subscription.current_period_end).toLocaleDateString()}</>}
        </div>
      </div>

      {status?.payment_status === "paid" && (
        <div className="card-flat p-4 border-l-4" style={{ borderLeftColor: "var(--span-success)" }} data-testid="payment-success">
          <div className="font-display font-bold text-[16px]">Payment received · plan activated 🎉</div>
          <div className="text-[12px] text-[var(--span-text-2)] mt-1">Your SPAN {status.amount_total/100} {status.currency.toUpperCase()} subscription is now active.</div>
        </div>
      )}
      {status && status.payment_status !== "paid" && (
        <div className="card-flat p-4 border-l-4" style={{ borderLeftColor: "var(--span-warning)" }}>
          <div className="font-display font-bold text-[16px]">Checking payment status…</div>
          <div className="text-[12px] text-[var(--span-text-2)] mt-1">{status.status} · {status.payment_status}</div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {Object.entries(plans).map(([id, p]) => {
          const current = me?.plan_id === id;
          const isPro = id === "pro";
          return (
            <div key={id} className={`card-flat p-6 relative ${isPro ? "border-2" : ""}`} style={isPro ? { borderColor: "var(--span-text)" } : {}} data-testid={`plan-${id}`}>
              {isPro && (
                <div className="absolute -top-3 left-6 bg-black text-white px-2 py-1 text-[10px] font-mono uppercase tracking-[0.18em] flex items-center gap-1">
                  <Sparkle size={10} weight="fill" /> Most popular
                </div>
              )}
              <div className="label-eyebrow">{p.interval === "free" ? "Free tier" : `Per ${p.interval}`}</div>
              <div className="font-display font-bold text-[26px] mt-1">{p.name}</div>
              <div className="font-display font-bold text-[40px] mt-3 tnum leading-none">
                {p.price_inr === 0 ? "Free" : fmtMoney(p.price_inr, "INR")}
                {p.price_inr > 0 && <span className="text-[14px] text-[var(--span-text-2)] font-normal"> /{p.interval}</span>}
              </div>
              <ul className="mt-5 space-y-2 min-h-[200px]">
                {p.features.map((f, i) => (
                  <li key={i} className="text-[13px] flex items-start gap-2">
                    <Check size={14} weight="bold" className="text-[var(--span-success)] mt-0.5 shrink-0"/>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                data-testid={`subscribe-${id}`}
                disabled={current || busy === id}
                onClick={() => subscribe(id)}
                className={`mt-5 w-full ${isPro ? "span-btn" : "span-btn span-btn-ghost"} disabled:opacity-50`}
              >
                {current ? "✓ Current plan" : busy === id ? "Redirecting…" : <>{p.cta} <ArrowRight size={13}/></>}
              </button>
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-[var(--span-text-2)] font-mono mt-6">
        Test mode · Use Stripe test card 4242 4242 4242 4242 with any future expiry + any CVC.
      </p>
    </div>
  );
}
